import { Injectable } from '@angular/core';
import { DyfProductInfo } from '@order/models/dyf-view-model';
import { InspiredPicksProductInfo } from '@order/models/inspired-picks-view-model';
import { MerchZoneProductInfo } from '@order/models/merch-zone-view-model';

@Injectable({
  providedIn: 'root',
})
export class ProductsLoadedService {
  /**
   *
   * Check if any Product Number in an array matches a
   * productInfo.product.productNumber from another array
   * Note that productInfo.product can be undefined, so checking
   * productInfos length isn't a valid test for products loaded
   *
   * @param productNumbers
   * @param productInfos
   * @returns
   */
  static check(
    productNumbers: number[],
    productInfos:
      | DyfProductInfo[]
      | InspiredPicksProductInfo[]
      | MerchZoneProductInfo[],
  ): boolean {
    // If there are no product numbers given to search for, return false
    if (!productNumbers || !productNumbers.length) {
      return false;
    }
    // If there are no productInfos to search through, return false
    if (!productInfos || !productInfos.length) {
      return false;
    }
    const isFound = productNumbers.some(productNumber =>
      productInfos.some(
        productInfo =>
          !!productInfo.product &&
          productInfo.product.productNumber === productNumber,
      ),
    );
    return isFound;
  }

  /**
   * Check if a product in the dyfProductNumbers has an entry in dyfProductInfos
   * If not determine if the missing productNumber is already in the order.
   * @param dyfProductNumbers are the numbers for the day stored in state
   * @param dyfProductInfos these are derived in selectors
   * @param orderItemProductNumbers product numbers currently on the order
   * @returns
   */
  static areMissingDyfProductInfosOnOrder(
    dyfProductNumbers: number[],
    dyfProductInfos: DyfProductInfo[],
    orderItemProductNumbers: number[],
  ): boolean {
    let isMissingProductInfoOnOrder = false;
    if (!dyfProductNumbers?.length || !orderItemProductNumbers?.length) {
      return isMissingProductInfoOnOrder;
    }

    // Check every dyfProductNumber, and if even one of them does
    // not have a dyfProductInfo entry and it's not on the order
    // return true
    dyfProductNumbers.forEach(dyfProductNumber => {
      if (!isMissingProductInfoOnOrder) {
        // does this product number have a dyfProductInfo match
        if (
          !!dyfProductInfos?.length ||
          !dyfProductInfos?.some(
            dyfProductInfo =>
              dyfProductInfo.product.productNumber === dyfProductNumber,
          )
        ) {
          // is this missing product number on the order already
          if (orderItemProductNumbers.includes(dyfProductNumber)) {
            isMissingProductInfoOnOrder = true;
          }
        }
      }
    });
    return isMissingProductInfoOnOrder;
  }
  // similar to check(), except stricter
  // checks that the first count of product numbers are loaded in the productInfos
  static checkEvery(
    productNumbers: number[],
    count,
    productInfos:
      | DyfProductInfo[]
      | InspiredPicksProductInfo[]
      | MerchZoneProductInfo[],
  ): boolean {
    // If there are no product numbers given to search for, return false
    if (!productNumbers || !productNumbers.length) {
      return false;
    }
    // If there are no productInfos to search through, return false
    if (!productInfos || !productInfos.length) {
      return false;
    }

    const loadedProductNumbers = productInfos.map(
      productInfo => productInfo.product.productNumber,
    );
    for (
      let index = 0;
      index < Math.min(productNumbers.length, count);
      index++
    ) {
      if (
        !loadedProductNumbers.find(
          productNumber => productNumber === productNumbers[index],
        )
      ) {
        return false;
      }
    }

    return true;
  }
}
